
import { useState } from "react";
import { useForm } from "react-hook-form";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useQueryClient } from "@tanstack/react-query";
import { ScrollArea } from "@/components/ui/scroll-area";
import { MovementForm } from "./MovementForm";
import { useQuery } from "@tanstack/react-query";
import * as z from "zod";
import { zodResolver } from "@hookform/resolvers/zod";

interface ProductFormProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  periodId: string;
  product?: {
    id: string;
    name: string;
    description: string;
    code: string | null;
    dosage: string;
    packaging: string;
    unit_price: number | null;
    minimum_threshold: number | null;
    manufacturer?: string | null;
  };
}

// Schéma de validation avec zod
const productSchema = z.object({
  name: z.string().min(1, "Le nom est requis"),
  description: z.string().optional(),
  code: z.string().optional(),
  dosage: z.string().min(1, "Le dosage est requis"),
  packaging: z.string().min(1, "Le conditionnement est requis"),
  unit_price: z.number()
    .min(0, "Le prix ne peut pas être négatif")
    .nonnegative("Le prix doit être un nombre positif")
    .optional()
    .nullable(),
  minimum_threshold: z.number()
    .min(0, "Le seuil minimum ne peut pas être négatif")
    .nonnegative("Le seuil minimum doit être un nombre positif")
    .optional()
    .nullable(),
  manufacturer: z.string().optional(),
});

type FormValues = z.infer<typeof productSchema>;

export const ProductForm = ({ open, onOpenChange, periodId, product }: ProductFormProps) => {
  const [isLoading, setIsLoading] = useState(false);
  const [showMovementForm, setShowMovementForm] = useState(false);
  const [newProductId, setNewProductId] = useState<string | null>(null);
  const { toast } = useToast();
  const queryClient = useQueryClient();
  
  const form = useForm<FormValues>({
    resolver: zodResolver(productSchema),
    defaultValues: {
      name: product?.name || "",
      description: product?.description || "",
      code: product?.code || "",
      dosage: product?.dosage || "",
      packaging: product?.packaging || "",
      unit_price: product?.unit_price || 0,
      minimum_threshold: product?.minimum_threshold || 0,
      manufacturer: product?.manufacturer || "",
    },
  });

  // Récupérer le stock actuel du produit s'il existe
  const { data: currentStock } = useQuery({
    queryKey: ['product-stock', product?.id],
    queryFn: async () => {
      if (!product?.id) return null;
      const { data } = await supabase
        .from('inventory_movements')
        .select('current_stock')
        .eq('product_id', product.id)
        .order('created_at', { ascending: false })
        .limit(1)
        .single();
      return data?.current_stock || 0;
    },
    enabled: !!product?.id,
  });

  const onSubmit = async (values: FormValues) => {
    setIsLoading(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        toast({
          variant: "destructive",
          title: "Erreur",
          description: "Vous devez être connecté pour ajouter un produit",
        });
        return;
      }

      const productData = {
        name: values.name,
        description: values.description,
        code: values.code,
        dosage: values.dosage,
        packaging: values.packaging,
        unit_price: values.unit_price,
        minimum_threshold: values.minimum_threshold,
        manufacturer: values.manufacturer,
        period_id: periodId,
        professional_id: user.id,
      };

      if (product) {
        const { error } = await supabase
          .from("inventory_products")
          .update(productData)
          .eq("id", product.id);

        if (error) throw error;

        toast({
          title: "Produit mis à jour",
          description: "Le produit a été mis à jour avec succès"
        });
      } else {
        const { data: newProduct, error } = await supabase
          .from("inventory_products")
          .insert(productData)
          .select()
          .single();

        if (error) throw error;

        if (newProduct) {
          setNewProductId(newProduct.id);
          setShowMovementForm(true);
        }

        toast({
          title: "Produit ajouté",
          description: "Le produit a été ajouté avec succès. Vous pouvez maintenant ajouter le stock initial."
        });
      }
      
      queryClient.invalidateQueries({ queryKey: ["inventory-products"] });
      queryClient.invalidateQueries({ queryKey: ["professional-products"] });
      
      if (!showMovementForm && !product) {
        onOpenChange(false);
        form.reset();
      }
    } catch (error) {
      console.error("Error in onSubmit:", error);
      toast({
        variant: "destructive",
        title: "Erreur",
        description: product
          ? "Impossible de mettre à jour le produit"
          : "Impossible d'ajouter le produit",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleMovementFormClose = () => {
    setShowMovementForm(false);
    onOpenChange(false);  // Close the product form after movement is added
    form.reset();
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-[425px] h-[90vh]">
          <DialogHeader>
            <DialogTitle>
              {product ? "Modifier le produit" : "Ajouter un produit"}
            </DialogTitle>
          </DialogHeader>
          <ScrollArea className="h-[calc(90vh-100px)] px-4">
            <Form {...form}>
              <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
                <FormField
                  control={form.control}
                  name="name"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Nom du produit</FormLabel>
                      <FormControl>
                        <Input required {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                
                <FormField
                  control={form.control}
                  name="description"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Description</FormLabel>
                      <FormControl>
                        <Textarea {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                
                <FormField
                  control={form.control}
                  name="code"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Code/Référence</FormLabel>
                      <FormControl>
                        <Input {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                
                <FormField
                  control={form.control}
                  name="manufacturer"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Fabricant</FormLabel>
                      <FormControl>
                        <Input {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                
                <FormField
                  control={form.control}
                  name="dosage"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Dosage</FormLabel>
                      <FormControl>
                        <Input required {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                
                <FormField
                  control={form.control}
                  name="packaging"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Conditionnement</FormLabel>
                      <FormControl>
                        <Input required {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                
                <FormField
                  control={form.control}
                  name="unit_price"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Prix unitaire (€)</FormLabel>
                      <FormControl>
                        <Input 
                          type="number" 
                          min={0} 
                          step={0.01}
                          {...field} 
                          onChange={e => field.onChange(parseFloat(e.target.value))}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                
                <FormField
                  control={form.control}
                  name="minimum_threshold"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Seuil d'alerte (stock minimum)</FormLabel>
                      <FormControl>
                        <Input 
                          type="number" 
                          min={0}
                          {...field} 
                          onChange={e => field.onChange(parseInt(e.target.value))}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                
                {product && (
                  <div className="p-4 bg-muted rounded-md">
                    <p className="font-medium">Stock actuel: {currentStock || 0} unité(s)</p>
                  </div>
                )}
                
                <div className="flex justify-end gap-2">
                  <Button 
                    type="button" 
                    variant="outline" 
                    onClick={() => onOpenChange(false)}
                  >
                    Annuler
                  </Button>
                  <Button 
                    type="submit" 
                    disabled={isLoading}
                  >
                    {isLoading ? "Enregistrement..." : (product ? "Modifier" : "Ajouter")}
                  </Button>
                </div>
              </form>
            </Form>
          </ScrollArea>
        </DialogContent>
      </Dialog>
      
      {showMovementForm && newProductId && (
        <MovementForm
          open={showMovementForm}
          onOpenChange={handleMovementFormClose}
          productId={newProductId}
          periodId={periodId}
        />
      )}
    </>
  );
};
